import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiLoader, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';
import ConfirmModal from '../../common/ConfirmModal';

// Dichiarazione dell'interfaccia delle proprietà attese
interface ListAnswersProps {
  onEditAnswer: (answerId: number) => void; // Handler per attivare la modifica nel genitore
}

interface Answer {
  id: number;
  testo: string;
  created_at: string;
}

const ListAnswers: React.FC<ListAnswersProps> = ({ onEditAnswer }) => {
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [isAdding, setIsAdding] = useState(false);
  const [newAnswerText, setNewAnswerText] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Answer | null>(null);

  useEffect(() => {
    fetchAnswers();
  }, []);

  const fetchAnswers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/answers`);
      setAnswers(response.data);
    } catch (error) {
      setError("Errore durante il caricamento delle risposte.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (answer: Answer) => {
    onEditAnswer(answer.id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/answers/${deleteConfirm.id}`);
      setAnswers((prev) => prev.filter(a => a.id !== deleteConfirm.id));
      setSuccessMessage("Risposta eliminata con successo!");
      setError(null);
      setTimeout(() => setSuccessMessage(null), 2000);
      fetchAnswers();
    } catch (error) {
      setError("Errore durante la cancellazione della risposta.");
      setTimeout(() => setError(null), 2000);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const confirmAdd = async () => {
    if (!newAnswerText.trim()) {
      setError("Il testo della risposta non può essere vuoto.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    const exists = answers.some(answer => answer.testo === newAnswerText);
    if (exists) {
      setError("La risposta esiste già.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/answer`, { testo: newAnswerText });
      setNewAnswerText('');
      setIsAdding(false);
      setSuccessMessage("Risposta aggiunta con successo!");
      setError(null);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchAnswers();
    } catch (error) {
      setError("Errore durante l'aggiunta della risposta.");
      setTimeout(() => setError(null), 3000);
    }
  };

  const filteredAnswers = answers.filter(answer =>
    answer.testo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedAnswers = [...filteredAnswers].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const totalPages = Math.ceil(sortedAnswers.length / itemsPerPage);
  const displayedAnswers = sortedAnswers.slice((page - 1) * itemsPerPage, page * itemsPerPage);



  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200/80">
      <div className="px-6 py-4 border-b border-gray-200 my-4">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">Lista delle risposte</h2>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Cerca per risposta..."
            className="w-1/2 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <div>
            {!isAdding && (
              <button
                onClick={() => setIsAdding(true)}
                className="ml-4 px-4 py-2 bg-[rgb(51,112,183)] text-white rounded-lg hover:bg-[rgb(89,96,211)]"
              >
                Aggiungi una risposta
              </button>
            )}
          </div>
        </div>

        {successMessage && (
          <div className="mt-2 mb-4 px-4 py-2 bg-green-100 text-green-700 text-sm rounded flex items-center justify-between">
            {successMessage}
            <button onClick={() => setSuccessMessage(null)} className="ml-2">
              <FiX className="w-4 h-4 text-green-700" />
            </button>
          </div>
        )}
        {error && (
          <div className="mt-2 mb-4 px-4 py-2 bg-red-100 text-red-700 text-sm rounded flex items-center justify-between">
            {error}
            <button onClick={() => setError(null)} className="ml-2">
              <FiX className="w-4 h-4 text-red-700" />
            </button>
          </div>
        )}
      </div>

      {isAdding && (
        <div className="px-4 py-2 flex items-center">
          <input
            type="text"
            value={newAnswerText}
            onChange={(e) => setNewAnswerText(e.target.value)}
            placeholder="Nuova risposta"
            className="border border-gray-300 rounded p-2 flex-1"
          />
          <button
            onClick={confirmAdd}
            className="ml-2 px-4 py-2 bg-[rgb(51,112,183)] text-white rounded-lg hover:bg-[rgb(89,96,211)]"
          >
            Aggiungi
          </button>
          <button
            onClick={() => setIsAdding(false)}
            className="ml-2 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
          >
            Annulla
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-[rgb(149,71,254)] via-[rgb(89,96,211)] to-[rgb(51,112,183)]">
            <tr className="border-b border-[rgb(51,112,183)]">
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Risposta</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Data di creazione</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">Azioni</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayedAnswers.map((answer, index) => (
              <tr key={answer.id} className="odd:bg-white even:bg-gray-50 hover:bg-gray-100 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{(page - 1) * itemsPerPage + index + 1}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{answer.testo}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(answer.created_at).toLocaleDateString()}</td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleEdit(answer)}
                      className="p-1 rounded-full hover:bg-indigo-50 text-indigo-600 transition-colors"
                      title="Modifica"
                    >
                      <FiEdit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(answer)}
                      className="p-1 rounded-full hover:bg-red-50 text-red-600 transition-colors"
                      title="Elimina"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {sortedAnswers.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-gray-500 text-sm">Nessuna risposta trovata</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-4 flex justify-between items-center">
        <button
          onClick={() => setPage(page > 1 ? page - 1 : 1)}
          disabled={page === 1}
          className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg"
        >
          Precedente
        </button>
        <span className="text-sm text-gray-500">Pagina {page} di {totalPages}</span>
        <button
          onClick={() => setPage(page < totalPages ? page + 1 : totalPages)}
          disabled={page === totalPages}
          className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-lg"
        >
          Successivo
        </button>
      </div>

      {deleteConfirm && (
        <ConfirmModal
          isOpen={deleteConfirm !== null}
          title="Conferma eliminazione"
          message="Sei sicuro di voler eliminare questa risposta?"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

export default ListAnswers;
